import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, FileText, Sparkles, X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminResumes = () => {
  const [resumes, setResumes] = useState([]);
  const [selectedResume, setSelectedResume] = useState(null);
  const [matches, setMatches] = useState(null);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [showMatchesModal, setShowMatchesModal] = useState(false);

  useEffect(() => {
    fetchResumes();
  }, []);

  const fetchResumes = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API}/admin/resumes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResumes(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const downloadResume = async (id, fileName) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API}/admin/resumes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { file_data, file_type } = response.data;
      const byteCharacters = atob(file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading:', error);
    }
  };

  const analyzeMatches = async (resume) => {
    setSelectedResume(resume);
    setLoadingMatches(true);
    setShowMatchesModal(true);
    
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.post(
        `${API}/admin/resumes/${resume.id}/match`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMatches(response.data);
      toast.success('AI analysis complete!');
    } catch (error) {
      console.error('Error analyzing matches:', error);
      toast.error('Failed to analyze matches');
      setMatches(null);
    } finally {
      setLoadingMatches(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-blue-600 bg-blue-100';
    if (score >= 40) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div data-testid="admin-resumes-page">
      <h1 className="text-3xl font-bold text-primary mb-8">Resume Submissions</h1>

      <div className="bg-white shadow-md">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Position</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Experience</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Submitted</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {resumes.map((resume) => (
                <tr key={resume.id} data-testid={`resume-row-${resume.id}`}>
                  <td className="px-6 py-4 text-sm font-medium text-primary">{resume.full_name}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{resume.email}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{resume.position}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{resume.experience}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {format(new Date(resume.submitted_at), 'MMM dd, yyyy')}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => downloadResume(resume.id, resume.file_name)}
                        data-testid={`download-resume-${resume.id}`}
                        className="inline-flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <Download size={18} className="mr-1" /> Download
                      </button>
                      <button
                        onClick={() => analyzeMatches(resume)}
                        data-testid={`analyze-resume-${resume.id}`}
                        className="inline-flex items-center text-purple-600 hover:text-purple-800"
                      >
                        <Sparkles size={18} className="mr-1" /> AI Match
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Matches Modal */}
      <Dialog open={showMatchesModal} onOpenChange={setShowMatchesModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="matches-modal">
          <DialogHeader>
            <DialogTitle>
              AI-Powered Job Matches {selectedResume && `for ${selectedResume.full_name}`}
            </DialogTitle>
          </DialogHeader>

          {loadingMatches ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Analyzing resume against all active jobs...</p>
            </div>
          ) : matches ? (
            <div className="space-y-6">
              <div className="bg-muted p-4 rounded">
                <p className="text-sm text-muted-foreground">
                  <strong>Candidate:</strong> {matches.candidate_name} | 
                  <strong className="ml-2">Position Sought:</strong> {matches.candidate_position}
                </p>
              </div>

              {matches.matches && matches.matches.length > 0 ? (
                <div className="space-y-4">
                  {matches.matches.map((match, index) => (
                    <div
                      key={index}
                      className="border border-slate-200 p-6 rounded hover:shadow-md transition-shadow"
                      data-testid={`match-card-${index}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-primary mb-1">{match.job_title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {match.job_location} • {match.job_sector}
                          </p>
                        </div>
                        <div className={`px-4 py-2 rounded font-bold text-2xl ${getScoreColor(match.match_score)}`}>
                          {match.match_score}%
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Matching Skills */}
                        {match.matching_skills && match.matching_skills.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-green-700 mb-2 flex items-center">
                              ✓ Matching Skills
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1">
                              {match.matching_skills.map((skill, i) => (
                                <li key={i} className="text-muted-foreground">{skill}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Missing Skills */}
                        {match.missing_skills && match.missing_skills.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-orange-700 mb-2 flex items-center">
                              ⚠ Skills Gap
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1">
                              {match.missing_skills.map((skill, i) => (
                                <li key={i} className="text-muted-foreground">{skill}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Strengths */}
                        {match.strengths && match.strengths.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-blue-700 mb-2 flex items-center">
                              💪 Key Strengths
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1">
                              {match.strengths.map((strength, i) => (
                                <li key={i} className="text-muted-foreground">{strength}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Concerns */}
                        {match.concerns && match.concerns.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-red-700 mb-2 flex items-center">
                              ⚠️ Concerns
                            </h4>
                            <ul className="list-disc list-inside text-sm space-y-1">
                              {match.concerns.map((concern, i) => (
                                <li key={i} className="text-muted-foreground">{concern}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Recommendations */}
                      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {match.candidate_recommendation && (
                          <div className="bg-blue-50 p-4 rounded">
                            <h4 className="font-semibold text-blue-900 mb-2">For Candidate</h4>
                            <p className="text-sm text-blue-800">{match.candidate_recommendation}</p>
                          </div>
                        )}
                        {match.recruiter_recommendation && (
                          <div className="bg-purple-50 p-4 rounded">
                            <h4 className="font-semibold text-purple-900 mb-2">For Recruiter</h4>
                            <p className="text-sm text-purple-800">{match.recruiter_recommendation}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  No active jobs to match against
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              Failed to load matches. Please try again.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminResumes;